import {
  SyncedProp,
  useEventListener,
  useFbp,
  useIsPreview,
  usePaygates,
  usePinterest,
  useRedirect,
  useSetting,
  useSnapChat,
  useSyncedState,
  useTtq
} from '@ui/hooks'
import { useEffect, useMemo, useState } from 'react'
import * as immutable from 'object-path-immutable'

import Countdown from '@ui/components/shared/Countdown'
import DropdownSelect from '@ui/components/shared/DropdownSelect'
import QuantityInput from '@ui/components/shared/QuantityInput'
import RadioButton from '@ui/components/shared/RadioButton'
import RandomNumber from '@ui/components/shared/RandomNumber'
import css from 'styled-jsx/css'
import { getCurrency, iconPath, isEmpty } from '@ui/helpers'
import { logger } from '@ui/analytics'
import { useNotiContext } from '../shared/Notification'
import { useRouter } from 'next/router'
import { genSid, preCalcOrder } from '@libs/client/helpers'
import {
  useLocalIsPuchase,
  useLocalOrder,
  useOrderService,
  usePriceChangedItems,
  useTrackingService
} from '@libs/client'
import CartModal from './cart/CartModal'
import Description from '../shared/Description'
import { useGtag } from '@ui/hooks/useGtag'
import { page_variants_items } from '@libs/common'

type ProductFormProps = {
  activeImageSyncedProp: SyncedProp<number>
  changedVariantImageSyncedProp: SyncedProp<number>
  isPayingWithPaypalSyncedProp?: SyncedProp<boolean>
}

interface ISelectVariantOptions {
  changeImage?: boolean
  redirect?: boolean
}

function initProperties(properties: S3Types.page_variants_properties) {
  const init: S3Types.item_properties = {}
  Object.keys(properties).forEach((key) => {
    init[key] = Object.entries(properties[key]?.values ?? {})?.[0]?.[1]?.code ?? ''
  })
  return init
}

function compareProperties(props1: S3Types.item_properties, props2: S3Types.item_properties) {
  let isEqual = true

  Object.keys(props1).forEach((key) => {
    if (props1[key] !== props2[key]) {
      isEqual = false
    }
  })

  return isEqual
}

function findVariant(
  properties: S3Types.item_properties,
  variantItems: S3Types.page_variants_items
) {
  const variantIds = Object.keys(variantItems)
  const foundVariantId = variantIds.find((variantId) =>
    compareProperties(properties, variantItems[variantId].properties ?? {})
  )
  return foundVariantId ? variantItems[foundVariantId] : undefined
}

export default function ProductForm({
  activeImageSyncedProp,
  changedVariantImageSyncedProp
}: ProductFormProps) {
  const router = useRouter()
  const subpath = router.query.subpath?.toString()
  const [sellpageSetting, setStore] = useSetting('sellpage')
  const isPreview = useIsPreview()
  const paygates = usePaygates()
  const paypalEx = paygates?.paypalex
  const creditCard = paygates?.creditcard
  const { fbpTrackViewContent, fbpTrackAddToCart, fbpTrackInitiateCheckout } = useFbp()
  const { pinterestTrackViewContent, pinterestTrackAddToCart } = usePinterest()
  const {
    snapchatTrackViewContent,
    snapchatTrackAddToCart,
    snapchatTrackInitiateCheckout
  } = useSnapChat()
  const {
    ttqTrackViewContent,
    ttqTrackAddToCart,
    ttqTrackInitiateCheckout,
    ttqTrackCheckout
  } = useTtq()
  const { gtagTrackViewItem, gtagTrackAddToCart, gtagTrackBeginCheckout } = useGtag()
  const redirect = useRedirect()
  const images = sellpageSetting?.gallery
  const configurations = sellpageSetting?.settings?.configurations
  const socialProof = sellpageSetting?.settings?.social_proof
  const variantItems = sellpageSetting?.variants?.items ?? {}
  const properties = sellpageSetting?.variants?.properties ?? {}
  const shortDescription = sellpageSetting?.parts?.content?.short
  const [localIsPuchase] = useLocalIsPuchase()

  const { trackingAddToCart, trackingViewItem } = useTrackingService()
  // const isPaypalCredit = false

  const [, setSyncedActiveImage] = activeImageSyncedProp
  const [, setSyncedChangedVariantImage] = changedVariantImageSyncedProp

  const [quantity, , getQuantitySyncedProp] = useSyncedState<number>(1)
  const [selectedVariant, setSelectedVariant] = useState<S3Types.page_variants_item | null>(
    initVariant(sellpageSetting?.variants, initProperties(properties), variantItems)
  )

  const [isSellable, setIsSellable] = useState<boolean>(selectedVariant?.sellable ?? true)
  const [isVariantNotFound, setIsVariantNotFound] = useState<boolean>(false)
  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false)
  const [isBuyingNow, setIsBuyingNow] = useState<boolean>(false)
  const [, setIsCheckingOut, getIsCheckingOutSyncedProp] = useSyncedState<boolean>(false)
  const [, setCartModalActive, getCartModalActiveSyncedProp] = useSyncedState<boolean>(false)
  const hasAvailableVariants = useMemo(
    () => Object.entries(variantItems ?? {}).some(([, variant]) => variant.sellable ?? true),
    [variantItems]
  )

  const { notiDispatch } = useNotiContext()

  const { initItems, addItems, updateItems } = useOrderService()
  const [localOrder, setLocalOrder] = useLocalOrder()

  const [priceChangedVariantItems, setPriceChangedVariantItems] = usePriceChangedItems()

  const onPropertyChanged = (prop: S3Types.item_properties) => {
    const selectedProps = selectedVariant?.properties
    const newProps = { ...selectedProps, ...prop }
    getSelectedVariant(newProps, { changeImage: true, redirect: true })
    logger.logProductPage('variant')
  }

  useEffect(() => {
    getInitSelectedVariant()

    //TODO: tracking
    if (!localIsPuchase) {
      trackingViewItem()
    }
    fbpTrackViewContent(selectedVariant?.default_price)
    pinterestTrackViewContent(selectedVariant?.default_price)
    snapchatTrackViewContent(selectedVariant?.default_price)
    ttqTrackViewContent(selectedVariant?.default_price)
    if (!router.query['utm_campaign']?.includes('+')) {
      gtagTrackViewItem(selectedVariant?.default_price)
    }
    // Analytics.track('PRODUCT_VIEW')
  }, [])

  useEventListener({
    type: 'visibilitychange',
    listener: onTabActive,
    options: { passive: true },
    element: typeof document !== 'undefined' ? document : undefined
  })

  useEffect(() => {
    logger.logProductPage('quantity')
  }, [quantity])

  function initVariant(
    variants?: S3Types.page_variants,
    properties?: S3Types.item_properties,
    variantItems?: page_variants_items
  ) {
    if (!variants) return null
    const queryVariant = getQuerySelectedVariant()
    if (queryVariant) {
      const selectedVariant = findVariant(queryVariant.properties, variantItems || {})
      return selectedVariant ?? Object.values(variants.items ?? {})?.[0] ?? null
    } else {
      const defaultId = variants.default_variant_id ?? ''
      if (defaultId) {
        return variants.items?.[defaultId]
      } else {
        const selectedVariant = findVariant(properties || {}, variantItems || {})
        return selectedVariant ?? Object.values(variants.items ?? {})?.[0] ?? null
      }
    }
  }

  function getInitSelectedVariant() {
    const queryVariant = getQuerySelectedVariant()
    // if (!queryVariant) queryVariant = selectedVariant
    // TODO init selected variant
    // let lastVariant = null
    // if (LocalVariant.variant) {
    //   lastVariant = LocalVariant.variant
    //   setQuantity(lastVariant.quantity)
    // }
    // const initSelectedVariant = lastVariant || queryVariant
    const initSelectedVariant = queryVariant
    if (initSelectedVariant?.properties) {
      const isInitProps = compareProperties(
        initSelectedVariant.properties,
        initProperties(properties)
      )
      if (isInitProps) {
        getSelectedVariant(initSelectedVariant.properties, {
          changeImage: true,
          redirect: false
        })
      } else {
        getSelectedVariant(initSelectedVariant.properties, {
          changeImage: true,
          redirect: false
        })
      }
    } else {
      getSelectedVariant(initProperties(properties))
    }
  }

  function getQuerySelectedVariant() {
    const variantId = router.query.variant?.toString()

    if (!variantId) {
      return null
    }

    return variantItems?.[variantId]
  }

  function getSelectedVariant(
    properties: S3Types.item_properties,
    options?: ISelectVariantOptions
  ) {
    const selectedVariant = findVariant(properties, variantItems)
    if (!selectedVariant) {
      setIsSellable(false)
      setIsVariantNotFound(true)
      redirect(`${subpath ? `/${subpath}` : ''}`, {
        query: { ...router.query, variant: undefined },
        routerOptions: { scroll: false },
        keepQuery: false,
        keepSlug: false
      })
      return null
    }
    setIsVariantNotFound(false)
    setIsSellable(selectedVariant.sellable ?? true)
    setSelectedVariant(selectedVariant)
    if (options?.changeImage) {
      let index = images?.findIndex((image) => image.includes(selectedVariant.logo ?? '')) ?? 0
      index = index < 0 ? 0 : index
      setSyncedActiveImage(index)
      setSyncedChangedVariantImage(index)
    }
    if (options?.redirect && selectedVariant?.id) {
      redirect(`${subpath ? `/${subpath}` : ''}`, {
        query: {
          ...router.query,
          variant: selectedVariant.id
        },
        routerOptions: { scroll: false },
        keepQuery: false,
        keepSlug: false
      })
    }
  }

  function onTabActive() {
    if (document.visibilityState === 'visible') {
      /* const localCart = new LocalCart(
        this.isPreview ? (this.$route.query.pph as string) : undefined
      ) */
      // if (JSON.stringify(orderVariants) !== JSON.stringify(LocalCart.cartVariants)) {
      //   setOrderVariants(LocalCart.cartVariants)
      // }
    }
  }

  async function onAddToCart(showCartModal = true) {
    if (!isSellable) {
      return false
    }

    if (isPreview) {
      return notiDispatch({
        payload: {
          content: `You couldn't add to cart in preview mode`,
          type: 'is-warning'
        },
        type: 'REMOVE_ALL_AND_ADD'
      })
    }

    setIsAddingToCart(true)
    // if (
    //   LocalOrder?.order?.status === OrderStatus.COMPLETED ||
    //   LocalOrder?.order?.status === OrderStatus.CONFIRMED
    // ) {
    //   LocalOrder.resetAll()
    // }

    try {
      // existedItem
      //   ? await updateItems(
      //       [{ id: existedItem.id, quantity: (existedItem?.quantity ?? 0) + quantity }],
      //       true
      //     )
      //   : await addItems([newOrderItem()], false, true)

      //TODO: tracking
      fbpTrackAddToCart(selectedVariant?.default_price, quantity)
      pinterestTrackAddToCart(selectedVariant?.default_price, quantity)
      snapchatTrackAddToCart(selectedVariant?.default_price, quantity)
      ttqTrackAddToCart(selectedVariant?.default_price, quantity)
      gtagTrackAddToCart(selectedVariant, quantity)
      if (localIsPuchase) {
        trackingViewItem()
      }
      trackingAddToCart(selectedVariant, quantity)
      // Analytics.track('ADD_TO_CART', {
      //   extra: { variant: this.selectedVariant }
      // })
      // logger.logProductPage('addCart')
      logger.logProductEvent('add_to_cart')

      // ManyChat.removeManyChat()

      setTimeout(async () => {
        const existedItem = localOrder?.items?.find(
          (item) => item.variant_id === selectedVariant?.id
        )

        existedItem
          ? updateItems([{ id: existedItem.id, quantity: (existedItem?.quantity ?? 0) + quantity }])
          : addItems([newOrderItem()])
        setIsAddingToCart(false)
        // show cart modal
        setCartModalActive(showCartModal)
      }, 500)
    } catch (error) {
      console.error('Add item error', error)
      notiDispatch({
        payload: {
          content: `Cannot add items to cart`,
          type: 'is-danger'
        },
        type: 'REMOVE_ALL_AND_ADD'
      })
      setLocalOrder(null)
      setIsAddingToCart(false)
    }
  }

  function newOrderItem(): Types.OrderItem {
    return {
      id: genSid(),
      variant_id: selectedVariant?.id,
      properties: selectedVariant?.properties,
      price: selectedVariant?.default_price,
      compare_price: selectedVariant?.compare_price,
      quantity: quantity,
      imageUrl: selectedVariant?.logo
    }
  }

  async function onCheckout() {
    if (isPreview) {
      return notiDispatch({
        payload: {
          content: `You couldn't checkout in preview mode`,
          type: 'is-warning'
        },
        type: 'REMOVE_ALL_AND_ADD'
      })
    }

    if (!creditCard && !paypalEx) {
      return notiDispatch({
        payload: {
          content: `The merchant account has been reached the limitation of payment processing`,
          type: 'is-danger'
        },
        type: 'REMOVE_ALL_AND_ADD'
      })
    }

    setIsCheckingOut(true)

    try {
      // const order = await OrderSvc.create(orderVariants, shopId, domain)

      // Analytics.track('CHECKOUT', {
      //   extra: { order, checkoutNow: false }
      // })

      logger.logCart('buy')

      const result = await initItems(localOrder?.items ?? [])

      fbpTrackInitiateCheckout(result?.items, result?.items_amount)
      snapchatTrackInitiateCheckout(result?.items, result?.items_amount)
      ttqTrackInitiateCheckout(result?.items, result?.items_amount)
      ttqTrackCheckout(result?.items, result?.items_amount)
      gtagTrackBeginCheckout(result?.items, result?.items_amount)

      redirect('/checkout')
    } catch (err) {
      console.error('Create order error', err)
      setIsCheckingOut(false)

      const message = 'Checkout error. Please try again later'
      const notifyContent =
        'The merchant account has been reached the limitation of payment processing'

      if (err?.response?.data?.error === message) {
        notiDispatch({
          payload: { content: notifyContent, type: 'is-danger' },
          type: 'REMOVE_ALL_AND_ADD'
        })
        // captureApiException(this, err, 'Disabled checkout', message)
      } else {
        notiDispatch({
          payload: { content: 'Checkout failure!', type: 'is-danger' },
          type: 'REMOVE_ALL_AND_ADD'
        })
        // captureApiException(this, err, 'Checkout failure')
      }
    }
  }

  async function onBuyNow() {
    if (isPreview) {
      return notiDispatch({
        payload: {
          content: `You couldn't checkout in preview mode`,
          type: 'is-warning'
        },
        type: 'REMOVE_ALL_AND_ADD'
      })
    }

    if (!isSellable) {
      return notiDispatch({
        payload: {
          content: `You couldn't buy a sold out item`,
          type: 'is-warning'
        },
        type: 'REMOVE_ALL_AND_ADD'
      })
    }

    if (!creditCard && !paypalEx) {
      return notiDispatch({
        payload: {
          content: `The merchant account has been reached the limitation of payment processing`,
          type: 'is-danger'
        },
        type: 'REMOVE_ALL_AND_ADD'
      })
    }

    setIsBuyingNow(true)
    logger.logProductPage('buy')
    // if (
    //   LocalOrder?.nowOrder?.status === OrderStatus.COMPLETED ||
    //   LocalOrder?.order?.status === OrderStatus.CONFIRMED
    // ) {
    //   LocalOrder.resetAll()
    // }
    try {
      if (localIsPuchase) {
        trackingViewItem()
      }
      const result = await initItems([newOrderItem()], true)
      fbpTrackInitiateCheckout(result?.items, result?.amount)
      snapchatTrackInitiateCheckout(result?.items, result?.amount)
      ttqTrackInitiateCheckout(result?.items, result?.amount)
      ttqTrackCheckout(result?.items, result?.amount)
      gtagTrackBeginCheckout(result?.items, result?.amount)
      fbpTrackAddToCart(selectedVariant?.default_price, quantity)
      pinterestTrackAddToCart(selectedVariant?.default_price, quantity)
      snapchatTrackAddToCart(selectedVariant?.default_price, quantity)
      ttqTrackAddToCart(selectedVariant?.default_price, quantity)
      gtagTrackAddToCart(selectedVariant, quantity)
      trackingAddToCart(selectedVariant, quantity)

      // Analytics.track('CHECKOUT', {
      //   extra: { order, checkoutNow: true }
      // })

      redirect('/checkout')
    } catch (err) {
      console.error('Buy now error. Cannot create order', err)
      setIsBuyingNow(false)

      const message = 'Checkout error. Please try again later'
      const notifyContent =
        'The merchant account has been reached the limitation of payment processing'

      if (err?.response?.data?.error === message) {
        notiDispatch({
          payload: { content: notifyContent, type: 'is-danger' },
          type: 'REMOVE_ALL_AND_ADD'
        })
        // captureApiException(this, err, 'Disabled checkout', message)
      } else {
        notiDispatch({
          payload: { content: 'Checkout failure!', type: 'is-danger' },
          type: 'REMOVE_ALL_AND_ADD'
        })
        // captureApiException(this, err, 'Checkout failure')
      }
    }
  }

  /**
   * specific listener to update sellpage variant items price when price changed
   * Tickets: https://crossian.atlassian.net/wiki/spaces/tech/pages/312868959/SF+-+Price+Change
   */
  useEffect(() => {
    if (priceChangedVariantItems && !isEmpty(priceChangedVariantItems)) {
      console.warn(
        'Variant price has been changed',
        sellpageSetting?.settings.general.id,
        Object.keys(priceChangedVariantItems)
      )
      const slug = router.query.subpath?.toString()
      const path = `pages.${slug ? `${slug}.` : ''}setting.variants.items`
      const items = { ...variantItems }
      Object.entries(priceChangedVariantItems).forEach(([variant_id, item]) => {
        if (items[variant_id])
          items[variant_id] = { ...items[variant_id], default_price: item.default_price }
      })
      if (selectedVariant?.id && priceChangedVariantItems[selectedVariant.id])
        setSelectedVariant({
          ...selectedVariant,
          default_price: priceChangedVariantItems[selectedVariant.id].default_price
        })
      setStore((store: S3Types.store) => {
        const _store = immutable.set(store, path, { ...items })
        return _store
      })
      setPriceChangedVariantItems({})
    }
  }, [priceChangedVariantItems])

  /**
   * specific listener to update local order items price when variant items price changed
   * Tickets: https://crossian.atlassian.net/wiki/spaces/tech/pages/312868959/SF+-+Price+Change
   */
  useEffect(() => {
    if (sellpageSetting) {
      const temp = [...(localOrder?.items ?? [])]
      const variantItems = sellpageSetting.variants?.items ?? {}
      const items = temp
        .filter(
          (item) =>
            item.variant_id &&
            !!variantItems[item.variant_id] &&
            variantItems[item.variant_id].sellable
        )
        .map((item) => ({ ...item, price: variantItems[item.variant_id ?? '']?.default_price }))
      const newOrder = preCalcOrder(items)
      setLocalOrder((prev) => ({ ...prev, ...newOrder, items }))
    }
  }, [])

  return (
    <>
      <div className="product-form">
        <style jsx global>
          {globalStyle}
        </style>
        <div className="product__title">{sellpageSetting?.settings?.general?.title}</div>

        {isVariantNotFound && <div className="product__price">--</div>}
        {!isVariantNotFound && (
          <div className="product__price">
            {getCurrency(selectedVariant?.default_price ?? 0)}{' '}
            {sellpageSetting?.settings?.configurations?.show_compared_price &&
              !!selectedVariant?.default_price &&
              !!selectedVariant?.compare_price &&
              selectedVariant?.default_price < selectedVariant?.compare_price && (
                <del className="compared">{getCurrency(selectedVariant?.compare_price ?? 0)}</del>
              )}
          </div>
        )}

        {sellpageSetting?.settings?.configurations?.show_countdown &&
          socialProof?.countdown_time &&
          hasAvailableVariants && (
            <div className="countdown">
              <strong>Last Minute&nbsp;</strong>- Sale end in&nbsp;
              <strong className="has-text-link">
                <Countdown time={socialProof.countdown_time} className="is-inline" />
              </strong>
            </div>
          )}

        {shortDescription && (
          <Description
            className="product__short-description rich-text"
            description={shortDescription}
          />
        )}

        {configurations?.variant_display && (
          <>
            <div className="product__options">
              {configurations?.variant_display === 'Button'
                ? properties &&
                  Object.keys(properties).map((property) => {
                    if (properties?.[property].values) {
                      return (
                        <RadioButton
                          key={`radio_${property}`}
                          propertyName={property}
                          selectedProperty={selectedVariant?.properties?.[property] ?? ''}
                          values={Object.keys(properties?.[property].values ?? {})}
                          onPropertyChanged={(propertyCode) => {
                            onPropertyChanged({ [property]: propertyCode })
                          }}
                        />
                      )
                    } else return null
                  })
                : Object.keys(properties).map((property) => {
                    if (properties?.[property].values) {
                      return (
                        <DropdownSelect
                          key={`dropdown_${property}`}
                          propertyName={property}
                          selectedProperty={selectedVariant?.properties?.[property] ?? ''}
                          values={Object.keys(properties?.[property].values ?? {})}
                          onPropertyChanged={(propertyCode) => {
                            onPropertyChanged({ [property]: propertyCode })
                          }}
                        />
                      )
                    } else return null
                  })}
              {}
            </div>
            {sellpageSetting?.settings?.configurations?.show_instock && isSellable && (
              <div className="in-stock is-flex is-flex-direction-row mb-3">
                <img src={iconPath('check-single.svg')} alt="" className="mr-2" />
                <strong>In stock</strong>
              </div>
            )}
          </>
        )}

        <div className="cta-buttons field is-grouped is-grouped-multiline">
          {hasAvailableVariants && (
            <div className="control">
              <QuantityInput
                className="is-fullwidth-mobile"
                label=""
                valueSyncedProp={getQuantitySyncedProp()}
                isHorizontal={false}
              />
            </div>
          )}
          {configurations?.has_cart && (
            <>
              <div className="control">
                <button
                  className={[
                    'add-to-cart-button button--cta button is-primary',
                    ...(isAddingToCart ? ['is-loading'] : [])
                  ].join(' ')}
                  disabled={!isSellable}
                  onClick={() => onAddToCart()}
                >
                  <img src={iconPath('cart.svg')} alt="" />
                  <span className="add-to-cart-button__content">
                    {isSellable ? 'Add to cart' : 'Sold out'}
                  </span>
                </button>
              </div>
              <div style={{ display: isSellable ? 'block' : 'none' }} className="control">
                <button
                  className={[
                    'buy-now-button button--cta button is-link',
                    ...(isBuyingNow ? ['is-loading'] : [])
                  ].join(' ')}
                  disabled={!isSellable}
                  onClick={() => onBuyNow()}
                >
                  <img src={iconPath('card.svg')} alt="" />
                  Buy it now
                </button>
              </div>
            </>
          )}
          {!configurations?.has_cart && (
            <>
              <div className="control">
                <button
                  className={`buy-now-button button--cta button is-primary
            ${isBuyingNow ? 'is-loading' : ''}`}
                  disabled={!isSellable}
                  onClick={() => onBuyNow()}
                >
                  <img src={iconPath('card.svg')} alt="" />
                  <span className="pay-with-card-button__content">
                    {isSellable ? 'Buy it now' : 'Sold out'}
                  </span>
                </button>
              </div>
              {/* {isSellable && isPaypalCredit && (
              // TODO: PaypalButton
              <div className="control">
                <PaypalButton
                  :orderVariants="paypalOrderVariants"
                  :isPaying.sync="syncedIsPayingWithPaypal"
                  :isSellable="isSellable"
                  :isCheckoutNow="true"
                />
              </div>
            )} */}
            </>
          )}
        </div>

        {configurations?.show_accept_payment && (!!paypalEx || !!creditCard) && (
          <div className="accepted-payment mt-4 mb-3">
            <strong>Accepted Payment Methods</strong>
            <div className="payment-methods is-flex mt-2">
              {paypalEx && (
                <figure className="label__icon image">
                  <img src={iconPath('paypal_icon.svg')} alt="" />
                </figure>
              )}
              {creditCard &&
                creditCard.cards.map((card) => (
                  <figure key={card} className="label__icon image">
                    <img src={iconPath(`${card.toLowerCase()}.svg`)} alt="" />
                  </figure>
                ))}
            </div>
          </div>
        )}

        {sellpageSetting?.settings?.configurations?.show_social_proof && hasAvailableVariants && (
          <small className="visting-people">
            <span className="has-text-link">Limited stock!</span>{' '}
            <strong>
              <RandomNumber initValue={socialProof?.visiting_people ?? 0} />
            </strong>{' '}
            people are viewing this and{' '}
            <strong>
              <RandomNumber initValue={socialProof?.purchased_orders ?? 0} type="up" />
            </strong>{' '}
            purchased it.
          </small>
        )}

        {configurations?.has_cart && (
          <CartModal
            productTitle={sellpageSetting?.settings?.general?.title}
            activeSyncedProp={getCartModalActiveSyncedProp()}
            onCheckout={onCheckout}
            isCheckingOutSyncedProp={getIsCheckingOutSyncedProp()}
          />
        )}
      </div>
    </>
  )
}

const globalStyle = css.global`
  .product-form {
    .countdown {
      font-size: 16px;
      margin-bottom: 28px;
    }

    .accepted-payment {
      strong {
        font-size: 13px;
      }

      .label__icon {
        margin-right: 6px;

        img {
          width: unset;
          height: 20px;
        }
      }
    }

    .product {
      &__title {
        font-size: 32px;
        line-height: 36px;
        font-weight: bold;
        margin-bottom: 15px;

        @media screen and (max-width: 768px) {
          font-size: 24px;
        }
      }

      &__price {
        font-weight: 500;
        font-size: 24px;
        line-height: 35px;
        margin-bottom: 9px;

        .compared {
          color: #717171;
          font-weight: 300;
          font-size: 20px;
          margin-left: 5px;
        }
      }

      &__short-description {
        margin-bottom: 42px;
      }

      &__options {
        margin-bottom: 49px;

        @media screen and (max-width: 768px) {
          margin-bottom: 20px;
        }
      }
    }

    .in-stock {
      strong {
        font-size: 13px;
      }
    }

    .cta-buttons {
      margin-bottom: -4px;

      &.field.is-grouped {
        & > .control {
          &:not(:last-child) {
            margin-right: 10px;
          }

          & > .button {
            padding: 15px 18px;

            img {
              margin-right: 16px;
              width: 23px;
              height: 23px;
            }
          }
        }
      }

      @media screen and (max-width: 768px) {
        margin-bottom: 8px !important;

        & > .control {
          width: 100%;

          & > .button {
            width: 100%;
          }
        }
      }
    }

    .paypal-button {
      height: 55px;
      min-width: 158px;
    }

    .pay-with-card-button {
      &__content {
        width: 80px;
      }
    }

    .add-to-cart-button {
      &__content {
        width: 90px;
      }
    }
  }
`
